'use client';
import ButtonBigCall from '@components/button-big-call/ButtonBigCall';
import ParallaxMedia from '@components/parallax-media/ParallaxMedia';
import { changeActiveRooms, changeRoomsAreas } from '@lib/store/slices/ProjectSlice';
import { SafeHtmlRenderer } from '@lib/tools/helpers/SafeHtmlRenderer';
import { animated, useSpring } from '@react-spring/web';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { A11y, EffectCreative } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import style from './SeventhScreenGeneral.module.scss';

import 'swiper/css';
import 'swiper/css/effect-creative';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import classNames from 'classnames';

const getRoomType = item => {
  if (item.id === 0) return 'studio';
  if (item.id === 1) return '1room';
  return `${item.id}rooms`;
};

function SeventhScreenGeneral({ roomsAreas, isMobile }) {
  const [toggle2, setToggle2] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [swiper1, setSwiperInstance] = useState(null);

  const svgRef = useRef(null);

  const router = useRouter();

  const dispatch = useDispatch();

  const roomsInfo = useSelector(state => state.projectinfo.roomsInfo);

  const opacityStyle = useSpring({
    config: { duration: 400 },
    opacity: toggle2 ? 0 : 1,
    delay: 0,
  });

  useEffect(() => {
    dispatch(changeRoomsAreas(roomsAreas));
  }, [dispatch, roomsAreas]);

  useEffect(() => {
    if (roomsInfo) {
      setActiveTab(roomsInfo.filter(item => item.active === true));
    }
  }, [roomsInfo]);

  useEffect(() => {
    if (activeTab) {
      setToggle2(() => true);
      setTimeout(() => {
        setToggle2(() => false);
      }, 400);
    }
  }, [activeTab]);

  useEffect(() => {
    return () => {
      if (ScrollTrigger.getById('trigger4')) {
        ScrollTrigger.getById('trigger4').kill();
      }
    };
  }, []);

  useEffect(() => {
    if (activeTab && swiper1) {
      swiper1.slideTo(activeTab[0].id, 1000);
    }
  }, [activeTab, swiper1]);

  useEffect(() => {
    if (swiper1) {
      setTimeout(() => {
        dispatch(changeActiveRooms(0));
      }, 100);
    }
  }, [swiper1, dispatch]);

  return (
    <div data-scroll className={style.seventhScreenContainer} ref={svgRef} id='plans'>
      <div data-scroll data-scroll-speed='1' className={style.seventhScreenTitle}>
        <p>Планировки для лучших </p>
        <p>семейных моментов</p>
      </div>
      <div className={style.seventhScreenWrapper}>
        <div className={style.seventhScreenBoxDescription}>
          <div className={style.seventhScreenTabMenu}>
            <div className={style.seventhScreenTabMenuWrapper}>
              {roomsInfo?.map(item => (
                <button
                  key={item.id}
                  value={item.id}
                  className={item.active === true ? style.seventhScreenTabActive : style.seventhScreenTab}
                  onClick={() => {
                    dispatch(changeActiveRooms(item.id));
                  }}
                  data-testid={getRoomType(item)}
                >
                  {item.title}
                </button>
              ))}
            </div>
          </div>
          {isMobile && (
            <div className={style.seventhScreenImgContainer}>
              {activeTab && (
                <Swiper
                  modules={[A11y, EffectCreative]}
                  effect='creative'
                  initialSlide={0}
                  longSwipesMs={800}
                  speed={800}
                  creativeEffect={{
                    prev: {
                      shadow: true,
                      translate: ['-20%', 0, -1],
                    },
                    next: {
                      translate: ['100%', 0, 0],
                    },
                  }}
                  spaceBetween={10}
                  slidesPerView={1}
                  onSlideChange={swiper => {
                    dispatch(changeActiveRooms(swiper.activeIndex));
                  }}
                  onSwiper={swiper => {
                    swiper.allowSlideNext = false;
                    swiper.allowSlidePrev = false;
                    setSwiperInstance(swiper);
                  }}
                  className={`${style.sevenScreenImageBoxSwiper} ${style.seventhScreenSwiper}`}
                >
                  {roomsInfo?.map(slide => (
                    <SwiperSlide
                      key={slide.id}
                      index={slide.id}
                      className={classNames(
                        `seventhScreenGeneralSwiperSlide __${slide.id}`,
                        style.seventhScreenSwiperSlide,
                      )}
                    >
                      <ParallaxMedia src375={slide.srcMob} name='image' imagePlug={slide.plug} />
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}
            </div>
          )}
          <div className={style.seventhScreenDescriptionContainer}>
            {activeTab && !toggle2 && (
              <animated.div style={opacityStyle} className={style.seventhScreenDescription}>
                <SafeHtmlRenderer tag='div' content={activeTab[0].description || ''} />
              </animated.div>
            )}
          </div>
          {!isMobile && (
            <>
              <div className={style.seventhScreenInfo}>
                <p>От</p>
                {activeTab && !toggle2 && (
                  <animated.p style={opacityStyle} className={style.seventhScreenInfoArea}>
                    {activeTab[0].area} м<sup>2</sup>
                  </animated.p>
                )}
              </div>
              <ButtonBigCall
                text='Выбрать квартиру'
                className={style.seventhScreenBtn}
                handleFuction={() => {
                  router.push(`/list?rooms=${activeTab[0].id}`);
                }}
                testId='roomFilter'
              />
            </>
          )}
          {isMobile && (
            <div className={style.seventhScreenInfo}>
              <div className={style.seventhScreenInfoBoxMobile}>
                <p>От</p>
                {activeTab && !toggle2 && (
                  <animated.p style={opacityStyle} className={style.seventhScreenInfoArea}>
                    {activeTab[0].area} м<sup>2</sup>
                  </animated.p>
                )}
              </div>

              <ButtonBigCall
                text='Выбрать квартиру'
                className={style.seventhScreenBtn}
                handleFuction={() => {
                  router.push(`/list?rooms=${activeTab[0].id}`);
                }}
                testId='roomFilter'
              />
            </div>
          )}
          <p className={style.seventhScreenSubTextBottom}>
            Для бесшумного и чистого переезда предусмотрена предчистовая отделка.
          </p>
        </div>
        {!isMobile && (
          <div className={style.seventhScreenImgContainer}>
            {activeTab && (
              <Swiper
                modules={[A11y, EffectCreative]}
                effect='creative'
                initialSlide={0}
                longSwipesMs={800}
                speed={800}
                creativeEffect={{
                  prev: {
                    shadow: true,
                    translate: ['-20%', 0, -1],
                  },
                  next: {
                    translate: ['100%', 0, 0],
                  },
                }}
                spaceBetween={10}
                slidesPerView={1}
                onSlideChange={swiper => {
                  dispatch(changeActiveRooms(swiper.activeIndex));
                }}
                onSwiper={swiper => {
                  swiper.allowSlideNext = false;
                  swiper.allowSlidePrev = false;
                  setSwiperInstance(swiper);
                }}
                className={`${style.eighthScreenImageBox} ${style.swiper}`}
              >
                {roomsInfo?.map(slide => (
                  <SwiperSlide
                    key={slide.id}
                    index={slide.id}
                    className={classNames(`seventhScreenGeneralSwiperSlide __${slide.id}`, style.swiperSlide)}
                  >
                    <ParallaxMedia src={slide.src} name='image' imagePlug={slide.plug} height='100%' />
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SeventhScreenGeneral;
